import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface GPXPoint { lat: number; lon: number; ele?: number; time?: string; }
export interface GPXTrack {
  name?: string;
  points: GPXPoint[];
  deviceNames?: string[];
  stravaOwner?: string;
  cachedAt?: number;
}
export interface Activity {
  id: string; name: string; type: string; startDate: string;
  distance: number; movingTime: number; gpxTrack?: GPXTrack;
  deviceName?: string; stravaOwner?: string; cachedAt?: number;
  mergedFrom?: { activity1Id: string; activity2Id: string; waypoints: GPXPoint[]; sessionId: string; };
  mergedInto?: string[];
}
export interface MergeSession {
  id: string; activity1: Activity; activity2: Activity; waypoints: GPXPoint[]; mergedTrack?: GPXTrack;
}
export const STRAVA_CACHE_MAX_AGE = 24 * 60 * 60 * 1000;
export const DATA_CLEAR_SIGNAL = 'combine-data-cleared';

// Only known local uploads and merges of known local uploads may reach disk.
export function localActivities(activities: Activity[]): Activity[] {
  const localIds = new Set(activities.filter(a => a.id.startsWith('local-') && !a.stravaOwner && !a.gpxTrack?.stravaOwner && !a.mergedFrom).map(a => a.id));
  for (let n = 0; n < activities.length; n++) {
    let changed = false;
    for (const a of activities) {
      if (!a.stravaOwner && !a.gpxTrack?.stravaOwner && a.mergedFrom && localIds.has(a.mergedFrom.activity1Id) && localIds.has(a.mergedFrom.activity2Id) && !localIds.has(a.id)) {
        localIds.add(a.id); changed = true;
      }
    }
    if (!changed) break;
  }
  return activities.filter(a => localIds.has(a.id)).map(a => ({ ...a, mergedInto: a.mergedInto?.filter(id => localIds.has(id)) }));
}
function localSession(session: MergeSession | null, activities: Activity[]): MergeSession | null {
  if (!session) return null;
  const ids = new Set(localActivities(activities).map(a => a.id));
  return ids.has(session.activity1.id) && ids.has(session.activity2.id) && !session.activity1.stravaOwner && !session.activity1.gpxTrack?.stravaOwner && !session.activity2.stravaOwner && !session.activity2.gpxTrack?.stravaOwner && !session.mergedTrack?.stravaOwner ? session : null;
}
function fresh(a: { cachedAt?: number }, now = Date.now()): boolean {
  return typeof a.cachedAt === 'number' && a.cachedAt <= now && now - a.cachedAt < STRAVA_CACHE_MAX_AGE;
}
function allowed(a: Activity, athleteId: string | null, activities: Activity[]): boolean {
  if (localActivities(activities).some(local => local.id === a.id)) return true;
  return !!athleteId && a.stravaOwner === athleteId && fresh(a) && (!a.gpxTrack || (a.gpxTrack.stravaOwner === athleteId && fresh(a.gpxTrack)));
}
function validSession(session: MergeSession | null, athleteId: string | null, activities: Activity[]): MergeSession | null {
  if (!session) return null;
  if (![session.activity1.id, session.activity2.id].every(id => activities.some(a => a.id === id))) return null;
  if (!allowed(session.activity1, athleteId, activities) || !allowed(session.activity2, athleteId, activities)) return null;
  const track = session.mergedTrack;
  if (track?.stravaOwner && (track.stravaOwner !== athleteId || !fresh(track))) return null;
  return session;
}
function deriveActivity(activity: Activity, state: AppState): Activity {
  if (!activity.mergedFrom) return activity;
  const sources = [activity.mergedFrom.activity1Id, activity.mergedFrom.activity2Id].map(id => state.activities.find(a => a.id === id));
  const stravaSources = sources.filter((a): a is Activity => !!a?.stravaOwner);
  if (!stravaSources.length) return activity;
  const cachedAt = Math.min(...stravaSources.map(a => a.cachedAt ?? 0));
  return { ...activity, stravaOwner: stravaSources[0].stravaOwner, cachedAt, gpxTrack: activity.gpxTrack ? { ...activity.gpxTrack, stravaOwner: stravaSources[0].stravaOwner, cachedAt } : undefined };
}
interface AppState {
  athleteId: string | null; isSessionReady: boolean; dataEpoch: number;
  bindAthlete: (id: string | null) => void;
  clearAllData: () => void; clearExpiredData: () => void;
  activities: Activity[]; lastStravaFetch: number | null;
  markStravaFetch: () => void;
  setActivities: (activities: Activity[]) => void;
  addActivity: (activity: Activity) => void;
  updateActivity: (id: string, updates: Partial<Activity>) => void;
  deleteActivity: (id: string) => void;
  updateActivityMergeReferences: (id: string, mergedInto: string) => void;
  clearStravaActivities: () => void;
  currentSession: MergeSession | null;
  setCurrentSession: (session: MergeSession | null) => void;
  addWaypoint: (point: GPXPoint) => void;
  removeWaypoint: (index: number) => void;
  updateWaypoint: (index: number, point: GPXPoint) => void;
  setMergedTrack: (track: GPXTrack | undefined) => void;
  restoreMergeSession: (id: string) => void;
  isProcessing: boolean; setIsProcessing: (value: boolean) => void;
  error: string | null; setError: (error: string | null) => void;
}
export const useStore = create<AppState>()(persist((set, get) => ({
  athleteId: null, isSessionReady: false, dataEpoch: 0,
  bindAthlete: (athleteId) => set(state => {
    if (state.athleteId === athleteId) return { isSessionReady: true };
    const activities = localActivities(state.activities);
    return { athleteId, dataEpoch: state.dataEpoch + 1, isSessionReady: true, activities, currentSession: localSession(state.currentSession, activities), lastStravaFetch: null, error: null, isProcessing: false };
  }),
  clearAllData: () => {
    set(state => ({ dataEpoch: state.dataEpoch + 1, activities: [], currentSession: null, lastStravaFetch: null, error: null, isProcessing: false }));
    if (typeof window !== 'undefined') {
      try { window.localStorage.setItem(DATA_CLEAR_SIGNAL, `${Date.now()}-${Math.random()}`); } catch { /* Storage may be disabled. */ }
    }
  },
  clearExpiredData: () => set(state => {
    const activities = state.activities.filter(a => allowed(a, state.athleteId, state.activities));
    return { activities, currentSession: validSession(state.currentSession, state.athleteId, activities), lastStravaFetch: state.lastStravaFetch };
  }),
  activities: [], lastStravaFetch: null,
  markStravaFetch: () => set({ lastStravaFetch: Date.now() }),
  setActivities: activities => set(state => {
    const candidates = [...state.activities.filter(a => !a.stravaOwner), ...activities];
    const accepted = activities.filter(a => allowed(a, state.athleteId, candidates));
    return { activities: accepted, currentSession: validSession(state.currentSession, state.athleteId, accepted), lastStravaFetch: state.lastStravaFetch };
  }),
  addActivity: activity => set(state => {
    const next = deriveActivity(activity, state);
    const candidates = [...state.activities, next];
    return allowed(next, state.athleteId, candidates) ? { activities: candidates } : {};
  }),
  updateActivity: (id, updates) => set(state => {
    const candidates = state.activities.map(a => a.id === id ? deriveActivity({ ...a, ...updates }, state) : a);
    return { activities: candidates.filter(a => allowed(a, state.athleteId, candidates)) };
  }),
  deleteActivity: id => set(state => {
    const removed = new Set([id]);
    for (let n = 0; n < state.activities.length; n++) {
      for (const activity of state.activities) {
        if (activity.mergedFrom && (removed.has(activity.mergedFrom.activity1Id) || removed.has(activity.mergedFrom.activity2Id))) removed.add(activity.id);
      }
    }
    const activities = state.activities.filter(a => !removed.has(a.id)).map(a => ({ ...a, mergedInto: a.mergedInto?.filter(ref => !removed.has(ref)) }));
    const currentSession = state.currentSession && (removed.has(state.currentSession.activity1.id) || removed.has(state.currentSession.activity2.id)) ? null : state.currentSession;
    return { activities, currentSession };
  }),
  updateActivityMergeReferences: (id, mergedInto) => set(state => ({ activities: state.activities.map(a => a.id === id ? { ...a, mergedInto: [...new Set([...(a.mergedInto ?? []), mergedInto])] } : a) })),
  clearStravaActivities: () => set(state => {
    const activities = localActivities(state.activities);
    return { dataEpoch: state.dataEpoch + 1, activities, currentSession: localSession(state.currentSession, activities), lastStravaFetch: null };
  }),
  currentSession: null,
  setCurrentSession: currentSession => set(state => ({ currentSession: validSession(currentSession, state.athleteId, state.activities) })),
  addWaypoint: waypoint => set(state => ({ currentSession: state.currentSession ? { ...state.currentSession, waypoints: [...state.currentSession.waypoints, waypoint], mergedTrack: undefined } : null })),
  removeWaypoint: index => set(state => ({ currentSession: state.currentSession ? { ...state.currentSession, waypoints: state.currentSession.waypoints.filter((_, i) => i !== index), mergedTrack: undefined } : null })),
  updateWaypoint: (index, waypoint) => set(state => ({ currentSession: state.currentSession ? { ...state.currentSession, waypoints: state.currentSession.waypoints.map((p, i) => i === index ? waypoint : p), mergedTrack: undefined } : null })),
  setMergedTrack: track => set(state => {
    if (!state.currentSession) return {};
    const sources = [state.currentSession.activity1, state.currentSession.activity2].filter(a => a.stravaOwner);
    const mergedTrack = track && sources.length ? { ...track, stravaOwner: sources[0].stravaOwner, cachedAt: Math.min(...sources.map(a => a.cachedAt ?? 0)) } : track;
    const session = { ...state.currentSession, mergedTrack };
    return { currentSession: validSession(session, state.athleteId, state.activities) };
  }),
  restoreMergeSession: id => {
    const state = get(); const merged = state.activities.find(a => a.id === id);
    const from = merged?.mergedFrom;
    if (!from) return;
    const activity1 = state.activities.find(a => a.id === from.activity1Id);
    const activity2 = state.activities.find(a => a.id === from.activity2Id);
    if (activity1 && activity2) state.setCurrentSession({ id: from.sessionId, activity1, activity2, waypoints: from.waypoints, mergedTrack: merged?.gpxTrack });
  },
  isProcessing: false, setIsProcessing: isProcessing => set({ isProcessing }),
  error: null, setError: error => set({ error }),
}), {
  name: 'combine-storage', version: 2,
  partialize: state => {
    const activities = localActivities(state.activities);
    return { activities, currentSession: localSession(state.currentSession, activities) };
  },
  migrate: persisted => {
    const legacy = persisted as Partial<AppState> | undefined;
    const activities = localActivities(Array.isArray(legacy?.activities) ? legacy.activities : []);
    return { activities, currentSession: localSession(legacy?.currentSession ?? null, activities) };
  },
  merge: (persisted, current) => {
    const saved = persisted as Partial<AppState> | undefined;
    const savedLocal = localActivities(Array.isArray(saved?.activities) ? saved.activities : []);
    const runtime = current.activities.filter(a => a.stravaOwner && allowed(a, current.athleteId, current.activities));
    const activities = [...savedLocal, ...runtime];
    const currentSession = validSession(current.currentSession, current.athleteId, activities) ?? localSession(saved?.currentSession ?? null, savedLocal);
    return { ...current, activities, currentSession };
  },
}));
